import { getURLSearchParamsFromQuery } from '@schibsted-nmp/search-frontend-clients';
import React from 'react';
import { useMobileFilters } from '../components/filters/mobile-filters/MobileFiltersStore';
import { useSavedSearchId } from '../components/saved-search/SavedSearchStore';
import { useSearchParams } from '../data/SearchParamsStore';
import type { SearchParams } from './AppStateTypes';

type UrlState = {
    isMobileFiltersOpen: boolean;
    savedSearchId: number | null;
    searchParams: SearchParams;
};

export enum APP_URL_PARAMS {
    lat = 'lat',
    lon = 'lon',
    page = 'page',
    savedSearchId = 'stored-id',
    sort = 'sort',
}

export function useUrlState() {
    const { isMobileFiltersOpen, setIsMobileFiltersOpen } = useMobileFilters();
    const { savedSearchId, setSavedSearchId } = useSavedSearchId();
    const { searchParams, setSearchParams } = useSearchParams();

    const isOriginalUrlRef = React.useRef(true);

    React.useEffect(() => {
        const previousState: UrlState = window.history.state;
        const currentState = resolveState(
            searchParams,
            savedSearchId,
            isMobileFiltersOpen,
        );

        const oldUrl = new URL(window.location.href);
        const newUrl = new URL(oldUrl);

        const urlParams = getURLSearchParamsFromQuery(searchParams);
        newUrl.search = getUpdatedParams(urlParams, savedSearchId).toString();

        if (isOriginalUrlRef.current) {
            isOriginalUrlRef.current = false;

            if (currentState.isMobileFiltersOpen) {
                // If the mobile filters are open on the initial page load, push
                // the state to the history stack so that the user can close the
                // filters with the back button.
                window.history.replaceState(
                    { ...currentState, isMobileFiltersOpen: false },
                    '',
                    newUrl.toString(),
                );
                window.history.pushState(currentState, '', newUrl.toString());
            } else {
                // Otherwise, just replace the history state if this is the
                // initial page load, even if the URL and/or state has changed
                // for some reason.
                window.history.replaceState(
                    currentState,
                    '',
                    newUrl.toString(),
                );
            }

            return;
        }

        replacePreviousStateIfUpdateWithMobileFiltersOpen(
            currentState,
            previousState,
            newUrl,
        );

        const hasUpdatedPath = newUrl.pathname !== oldUrl.pathname;
        const hasUpdatedParams =
            getSignificantSearchParams(newUrl).toString() !==
            getSignificantSearchParams(oldUrl).toString();

        const isMobileFiltersOpened =
            !previousState.isMobileFiltersOpen &&
            currentState.isMobileFiltersOpen;
        const isMobileFiltersClosed =
            previousState.isMobileFiltersOpen &&
            !currentState.isMobileFiltersOpen;

        if (hasUpdatedPath || hasUpdatedParams || isMobileFiltersOpened) {
            window.history.pushState(currentState, '', newUrl.toString());
        } else if (isMobileFiltersClosed) {
            // TODO: open mobile filters with remaining history forward state
            window.history.back();
            window.history.replaceState(currentState, '', newUrl.toString());
        } else {
            window.history.replaceState(currentState, '', newUrl.toString());
        }
    }, [isMobileFiltersOpen, savedSearchId, searchParams]);

    React.useEffect(() => {
        function updateStateOnHistoryBack(ev: PopStateEvent) {
            const previousState = deserializeState(ev.state);
            setIsMobileFiltersOpen(previousState.isMobileFiltersOpen);
            setSavedSearchId(previousState.savedSearchId);
            setSearchParams(previousState.searchParams);
        }
        window.addEventListener('popstate', updateStateOnHistoryBack);
        return function cleanup() {
            window.removeEventListener('popstate', updateStateOnHistoryBack);
        };
    }, [setIsMobileFiltersOpen, setSavedSearchId, setSearchParams]);
}

function getUpdatedParams(
    searchParams: URLSearchParams,
    savedSearchId?: number | null,
) {
    const updatedParams = new URLSearchParams(searchParams);
    if (savedSearchId) {
        updatedParams.set(
            APP_URL_PARAMS.savedSearchId,
            savedSearchId.toString(),
        );
    } else {
        updatedParams.delete(APP_URL_PARAMS.savedSearchId);
    }
    updatedParams.sort();
    return updatedParams;
}

function getSignificantSearchParams(url: URL) {
    const urlParams = new URLSearchParams(url.searchParams);

    urlParams.delete(APP_URL_PARAMS.savedSearchId);

    return urlParams;
}

type SerializedSearchAppState = Omit<UrlState, ''>; // & {};

function resolveState(
    searchParams: SearchParams,
    savedSearchId: number | null,
    isMobileFiltersOpen: boolean,
): SerializedSearchAppState {
    return { searchParams, savedSearchId, isMobileFiltersOpen };
}

function deserializeState(state: SerializedSearchAppState): UrlState {
    return state;
}

function replacePreviousStateIfUpdateWithMobileFiltersOpen(
    currentState: UrlState,
    previousState: UrlState,
    newUrl: URL,
) {
    const isUpdateWithMobileFiltersOpen =
        previousState.isMobileFiltersOpen && currentState.isMobileFiltersOpen;

    if (isUpdateWithMobileFiltersOpen) {
        window.history.replaceState(
            { ...currentState, isMobileFiltersOpen: false },
            '',
            newUrl.toString(),
        );
    }
}
